import { AbstractProvider, provider as IProvider, RequestArguments } from 'web3-core';
import { WalletLinkProvider } from 'walletlink';
import {getAccounts, getMobileOperatingSystem, getNetworkToSwitch, wait} from './utils';
import { stateEventObserver } from './EventObserver';
import EventTypes from '../constants/eventTypes';
import { networks } from '../constants/networks';
import { INPAGE_KEY, WALLET_TYPES } from '../constants/wallet';

const chainIds = Object.values(networks).map((network) => network.chainId);
type TChainId = (typeof chainIds)[keyof typeof chainIds];

type IExtendedProvider = IProvider & {
  chainId: TChainId

  isRabby?: boolean
  isMathWallet?: boolean
  isSafePal?: boolean
  isTokenPocket?: boolean
  isMetaMask?: boolean
  isStatus?: boolean
  isImToken?: boolean
  isTrust?: boolean
  isToshi?: boolean
  isTokenary?: boolean

  request?(args: RequestArguments): Promise<any>;
} & keyof WalletLinkProvider & keyof AbstractProvider;

type IGetWeb3WindowProvider = () => IExtendedProvider;

// @ts-ignore TODO: getWeb3WindowProvider!
export const getWeb3WindowProvider: IGetWeb3WindowProvider = () => window.ethereum || window.web3?.currentProvider;

/**
 * Gets the name of the inpage provider
 *
 * @return {string|null}
 */
export const getInPageProviderName = () => {
  const provider = getWeb3WindowProvider();

  if (!provider) return null;

  if (provider.isRabby) return 'Rabby';
  if (provider.isMathWallet) return 'Math Wallet';
  if (provider.isSafePal) return 'SafePal';
  if (provider.isTokenPocket) return 'TokenPocket';
  if (provider.isMetaMask) return 'MetaMask';
  if (provider.isStatus) return 'Status';
  if (provider.isImToken) return 'imToken';
  if (provider.isTrust) return 'Trust';
  if (provider.isToshi) return 'Coinbase';
  if (provider.isTokenary) return 'Tokenary';
  if (navigator.userAgent.match(/Opera|OPR/)) return 'Opera';

  return 'Browser';
};

const isInPageProviderApproved = async () => {
  try {
    if (!window.ethereum || !window.ethereum.enable) return true;

    if (window.ethereum.selectedAddress) return true;

    const account = await window.ethereum.request({ method: 'eth_requestAccounts', params: [] });
    return !!account;
  } catch (err) {
    return false;
  }
};

/**
 * If MetaMask privacy is on, opens MetaMask modal to whitelist it
 *
 * @return {Promise<*>}
 */
const inPageProviderApprove = async () => {
  if (window.ethereum) return window.ethereum.enable();
};

// TODO Add events from (tokensfarm.app|hord.app)

let interval: NodeJS.Timeout;

export const removeInPageProviderListeners = () => {
  if (window.ethereum?.removeAllListeners) {
    window.ethereum.removeAllListeners();
  }
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
};

const subscribeInPageProviderEvents = async (networkId: number) => {
  const unlockedAccount = await getAccounts(window.ethereum);
  if (window.ethereum) {
    // @ts-ignore
    // TODO: https://stackoverflow.com/questions/69811616/how-to-change-global-type-declaration-to-already-declared-type-via-npm-package
    window.ethereum.autoRefreshOnNetworkChange = false;
  }
  if (window.ethereum?.on) {
    window.ethereum.on('accountsChanged', (accounts) => {
      removeInPageProviderListeners();
      if (accounts[0]) {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.inPageProvider });
      } else {
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
      }
    });

    window.ethereum.on('chainChanged', async (params) => {
      if (parseInt(params, 16) !== networkId) {
        removeInPageProviderListeners();
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        if (!document.hidden) {
          stateEventObserver.emit(EventTypes.CONNECT_REQUEST_COMPONENT, { networkId, connectionType: WALLET_TYPES.inPageProvider });
        }
      }
    });

    window.ethereum.on('disconnect', () => {
      removeInPageProviderListeners();
      stateEventObserver.emit(EventTypes.DISCONNECT, null);
    });
  } else {
    interval = setInterval(async () => {
      const address = await getAccounts(window.ethereum);
      if (address?.toLowerCase() !== unlockedAccount?.toLowerCase()) {
        removeInPageProviderListeners();
        stateEventObserver.emit(EventTypes.DISCONNECT, null);
        return clearInterval(interval);
      }
    }, 1000);
  }
};

export const connectInPageProvider = async (rpcUrl: string, networkId: number) => {
  const approved = await isInPageProviderApproved();
  if (!approved) {
    await inPageProviderApprove();
  }
  if (await isInPageProviderApproved()) {
    const provider = getWeb3WindowProvider();
    if (!provider) {
      throw new Error('inPageProvider is not injected! Please refresh page.');
    }
    let providerName = getInPageProviderName();
    if (providerName === 'Browser') {
      providerName = null;
    }
    if (provider.isMetaMask && provider.chainId !== networkId) {
      const network: any = getNetworkToSwitch(networkId);
      if (network) {
        const rpcUrls = [rpcUrl].concat(network.rpcUrls);
        const params = [{ ...network, rpcUrls }];
        try {
          const isMobileOS = getMobileOperatingSystem() !== 'unknown';
          if (isMobileOS) {
            localStorage.setItem(INPAGE_KEY, JSON.stringify({ rpcUrl, networkId, connectionType: WALLET_TYPES.inPageProvider }));
          }
          await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: `0x${(networkId).toString(16)}` }] });
          // To avoid Connection closed unexpectedly we need to wait 500ms
          await wait(500);
          subscribeInPageProviderEvents(networkId);
          return { provider, providerName };
        } catch (switchError) {
          const jsonRegexp = /("code"):[ ]?([-0-9]*)/gm;
          const errorCodes = JSON.stringify(switchError).match(jsonRegexp)?.map((match) => parseInt(match?.split(':')[1], 10));
          if (errorCodes?.includes(4902)) {
            try {
              await provider.request({ method: 'wallet_addEthereumChain', params });
              // To avoid Connection closed unexpectedly we need to wait 500ms
              await wait(500);
              subscribeInPageProviderEvents(networkId);
              return { provider, providerName };
            } catch (err) {
              throw new Error(`Your wallet network is not the same as in the app. Please, switch to the ${networks[networkId]?.chainName} network`);
            }
          }
        }
      }
      throw new Error(`Your wallet network is not the same as in the app. Please, switch to the ${networks[networkId]?.chainName} network`);
    }

    subscribeInPageProviderEvents(networkId);
    return { provider, providerName };
  }
  throw new Error('MetaMask is not approved!');
};
